import { useCallback, useMemo } from 'react';
import { Element } from '@thive/canvas';
import { Vector3 } from 'three';
import { t } from 'i18next';

import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { areasToFlatCollection, isHighwayArea } from '@/modules/floorplanDesign/helpers';
import { Area } from '@/modules/floorplanDesign/types';
import { useSnackbarStore } from '@/modules/snackbar/store/useSnackbarStore';

import { DistconServiceFactory } from '../service/DistconServiceFactory';
import { logger } from '@common/logging/logger';
import { resolveClickedGroupArea } from '@/modules/canvas/helpers/resolveClickedGroupArea';
import { isVisibleOnCanvas } from '@/modules/canvas/helpers/isVisibleOnCanvas';

export const useDistconClick = () => {
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
  const distconSvc = useMemo(() => DistconServiceFactory.getInstance().createDistconService(), []);

  const resolveClickedArea = useCallback((elementId: string): Area | undefined => {
    const state = useFloorplanDesignStore.getState();
    const area = state.getAreaById(elementId);
    if (area) return area;

    const areaOrSegment = state.getAreaOrSegmentById(elementId);
    if (!areaOrSegment) return undefined;

    if (areaOrSegment.type === 'area') return areaOrSegment.entity;

    return areasToFlatCollection(state.design.areas).find(
      (candidate) =>
        isHighwayArea(candidate) && candidate.segments.some((segment) => segment.id === elementId),
    );
  }, []);

  const distconClick = useCallback(
    (element: Element, clickedPosition: Vector3, isSecondClick: boolean) => {
      const area =
        resolveClickedArea(element.id) ??
        resolveClickedGroupArea(element, clickedPosition, {
          prioritizeSmallestArea: true,
          isAreaPickable: isVisibleOnCanvas,
        });

      if (!area) {
        logger.error(
          `Could not resolve clicked distcon ${
            isSecondClick ? 'target' : 'source'
          } for canvas element id: ${element.id}.`,
        );

        if (isSecondClick) {
          showSnackbar(
            t(
              'errors:connection.distant.invalid_target',
              'Invalid. Please select one of the highlighted areas, highways or highway-segments.',
            ),
          );
        }
        return;
      }

      if (isSecondClick) {
        distconSvc.endDistcon(area, clickedPosition);
        return;
      }

      distconSvc.startDistcon(area, clickedPosition);
    },
    [distconSvc, resolveClickedArea, showSnackbar],
  );

  return { distconClick };
};
